import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default hardcoded partners (fallback)
const defaultPartners = [
  { id: '1', name: 'Weatherhead Centre for International Affairs', shortName: 'W', colors: 'from-[#A51C30]/20 to-[#A51C30]/10', textColor: 'text-[#A51C30] dark:text-[#E8A8B3]' },
  { id: '2', name: 'Harvard Housing Studies', shortName: 'JCHS', colors: 'from-[#A51C30]/20 to-[#A51C30]/10', textColor: 'text-[#A51C30] dark:text-[#E8A8B3]' },
  { id: '3', name: 'City Science Lab', shortName: 'MIT', colors: 'from-red-600/20 to-red-800/10', textColor: 'text-red-600 dark:text-red-400' },
  { id: '4', name: 'Linneman Associates', shortName: 'LA', colors: 'from-primary/20 to-primary/10', textColor: 'text-primary' },
];

const PartnersSection = () => {
  const [cmsPartners, setCmsPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/partners`);
        if (response.ok) {
          const data = await response.json();
          setCmsPartners(data);
        }
      } catch (err) {
        console.error('Failed to fetch partners:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPartners();
  }, []);

  // Detect theme mode
  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem('theme') || 'dim';
      setIsDarkMode(theme === 'dark' || theme === 'dim');
    };
    
    checkTheme();
    window.addEventListener('storage', checkTheme);
    
    // Also watch for class changes on html element
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => {
      window.removeEventListener('storage', checkTheme);
      observer.disconnect();
    };
  }, []);

  // Get the appropriate logo URL based on theme
  const getLogoUrl = (partner) => {
    if (isDarkMode) {
      return partner.logo_url_dark || partner.logo_url;
    }
    return partner.logo_url_light || partner.logo_url;
  };

  const hasCustomPartners = cmsPartners.length > 0;

  return (
    <section className="relative z-10 py-20 px-6 md:px-12 lg:px-24" data-testid="partners-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
            Research Partners & Affiliations
          </span>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight">
            Powered by <span className="text-gradient-quantum">World-Class Research</span>
          </h2>
        </div>
        
        {/* Partner Logos Grid */}
        <div className="glass-card rounded-2xl p-8 mb-8">
          {hasCustomPartners ? (
            /* CMS Partners */
            <div className={`grid gap-6 items-center ${
              cmsPartners.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' :
              cmsPartners.length === 2 ? 'grid-cols-2 max-w-md mx-auto' :
              cmsPartners.length === 3 ? 'grid-cols-3 max-w-xl mx-auto' :
              cmsPartners.length === 4 ? 'grid-cols-2 md:grid-cols-4' :
              cmsPartners.length === 5 ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5' :
              'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
            }`}>
              {cmsPartners.map((partner) => (
                <a
                  key={partner.id}
                  href={partner.website || '#'}
                  target={partner.website ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="flex flex-col items-center text-center group"
                >
                  {getLogoUrl(partner) ? (
                    <div className="h-16 w-full flex items-center justify-center mb-2">
                      <img 
                        src={getLogoUrl(partner)} 
                        alt={partner.name}
                        className="h-12 w-auto max-w-full object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  ) : (
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-2 group-hover:from-primary/30 group-hover:to-primary/20 transition-all">
                      <span className="font-outfit font-bold text-lg text-primary">
                        {partner.name.split(' ').map(w => w[0]).join('').slice(0, 3)}
                      </span>
                    </div>
                  )}
                  <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors flex items-center gap-1">
                    {partner.name}
                    {partner.website && <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </span>
                </a>
              ))}
            </div>
          ) : (
            /* Default Hardcoded Partners */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 items-center justify-items-center">
              {/* Weatherhead Centre for International Affairs, Harvard University */}
              <div className="flex flex-col items-center text-center group">
                <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-[#A51C30]/20 to-[#A51C30]/10 flex items-center justify-center mb-2 group-hover:from-[#A51C30]/30 group-hover:to-[#A51C30]/20 transition-all">
                  <span className="font-outfit font-bold text-2xl text-[#A51C30] dark:text-[#E8A8B3]">W</span>
                </div>
                <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  Weatherhead Centre for International Affairs
                </span>
              </div>
              
              {/* Harvard JCHS */}
              <div className="flex flex-col items-center text-center group">
                <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-[#A51C30]/20 to-[#A51C30]/10 flex items-center justify-center mb-2 group-hover:from-[#A51C30]/30 group-hover:to-[#A51C30]/20 transition-all">
                  <span className="font-outfit font-bold text-sm text-[#A51C30] dark:text-[#E8A8B3]">JCHS</span>
                </div>
                <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  Harvard Housing Studies
                </span>
              </div>
              
              {/* MIT City Science */}
              <div className="flex flex-col items-center text-center group">
                <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-red-600/20 to-red-800/10 flex items-center justify-center mb-2 group-hover:from-red-600/30 group-hover:to-red-800/20 transition-all">
                  <span className="font-outfit font-bold text-lg text-red-600 dark:text-red-400">MIT</span>
                </div>
                <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  City Science Lab
                </span>
              </div>
              
              {/* Linneman Associates */}
              <div className="flex flex-col items-center text-center group">
                <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-2 group-hover:from-primary/30 group-hover:to-primary/20 transition-all">
                  <span className="font-outfit font-bold text-sm text-primary">LA</span>
                </div>
                <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  Linneman Associates
                </span>
              </div>
              
              {/* Global InTech */}
              <div className="flex flex-col items-center text-center group">
                <div className="h-16 flex items-center justify-center mb-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e3d4135c-91f6-4c21-8964-e67cd3b65643/artifacts/opw5twe6_final_logo_dark_transparent.png"
                    alt="Global InTech"
                    className="h-12 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity hidden dark:block"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e3d4135c-91f6-4c21-8964-e67cd3b65643/artifacts/azd2bq53_globalintech_logo_light.png"
                    alt="Global InTech"
                    className="h-12 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity dark:hidden block"
                  />
                </div>
                <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  Global InTech
                </span>
              </div>
              
              {/* Morency QSI Framework */}
              <div className="flex flex-col items-center text-center group">
                <div className="h-16 flex items-center justify-center mb-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e3d4135c-91f6-4c21-8964-e67cd3b65643/artifacts/ak0ll9ug_Morency_QSI_Framework_Logo_White.jpg"
                    alt="Morency QSI Framework"
                    className="h-12 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity hidden dark:block rounded"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e3d4135c-91f6-4c21-8964-e67cd3b65643/artifacts/qrk7fo0z_Morency_QSI_Framework_Logo_Dark.jpg"
                    alt="Morency QSI Framework"
                    className="h-12 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity dark:hidden block rounded"
                  />
                </div>
                <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  Morency Framework
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
